<template>
  <OverlayCard
    v-if="showConfirmDelete"
    class="overlay-confirm-delete"
    :class="{
      'overlay-confirm-delete__mobile': mobileScreen,
      'overlay-confirm-delete__fullscreen': isFeatureFullscreen && !mobileScreen
    }"
    @closeOverlay="showConfirmDelete = false"
  >
    <ConfirmDeleteAttachmentCard
      :buttonText="t('general__text__delete') + ' ' + t('general__data__ticket').toLowerCase()"
      @closeOverlay="showConfirmDelete = false"
      @confirmDelete="deleteSelectedTicket"
    />
  </OverlayCard>
  <div
    v-if="
      clickedItemType === 'realEstate' ||
      clickedItemType === 'building' ||
      clickedItemType === 'level' ||
      clickedItemType === 'room' ||
      clickedItemType == 'zone' ||
      clickedItemType == 'line' ||
      clickedItemType == 'poi'
    "
    id="tickets-dashboard"
    :class="{ fullscreen: isFeatureFullscreen && !mobileScreen, mobile: mobileScreen }"
  >
    <div class="button-group-container">
      <ButtonGroupNoIcons :tabOptions="tabOptions" @changeTab="changeTab" />
    </div>
    <div class="include-object-container">
      <!-- Disabled checkbox for realm -->
      <CheckBoxStandard
        class="include-object-container__checkbox"
        :checked="includeFinishedTickets"
        :disabled="selectedObject === 0"
        :title="t('general__data__tickets-include-finished')"
        @valueChanged="includeFinishedTickets = !includeFinishedTickets"
      />
    </div>
    <div class="include-object-container" style="margin-top: -8px">
      <!-- Disabled checkbox for realm -->
      <CheckBoxStandard
        class="include-object-container__checkbox"
        :checked="includeChildObject"
        :disabled="selectedObject === 0"
        :title="t('general__text__objects-include-child')"
        @valueChanged="includeChildObject = !includeChildObject"
      />
      <InfoLabel
        class="include-object-container__info"
        :info="t('tooltip__tickets-include-child-objects')"
      />
    </div>
    <div class="table-container">
      <PulseLoader
        :loading="isLoadingFirstPage"
        class="spinner"
        :class="{
          spinner__fullscreen: isFeatureFullscreen && !mobileScreen,
          spinner__mobile: mobileScreen
        }"
        color="#1d234f"
      />
      <TableTicketsMini
        v-if="!isLoadingFirstPage"
        :fields="fields"
        :hasArchiveButton="store.getters.hasLinkEditorAccess"
        :hasDeleteButton="store.getters.hasLinkAdminAccess"
        :items="piniaStore.ticketsList"
        :tabOption="activeTab"
        @archiveTicket="archiveSelectedTicket"
        @deleteTicket="showConfirmDelete = true"
        @loadMoreTickets="loadMoreTickets"
      />
    </div>
    <div
      class="bottom-container"
      :class="{
        'bottom-container__no-button':
          !store.getters.hasErrandCreatorAccess ||
          (store.getters.hasErrandCreatorAccess &&
            clickedItemType !== 'realEstate' &&
            clickedItemType !== 'building' &&
            clickedItemType !== 'level' &&
            clickedItemType !== 'room' &&
            clickedItemType != 'zone' &&
            clickedItemType != 'line' &&
            clickedItemType != 'poi')
      }"
    >
      <ButtonOutline
        v-if="
          store.getters.hasErrandCreatorAccess &&
          (clickedItemType === 'realEstate' ||
            clickedItemType === 'building' ||
            clickedItemType === 'level' ||
            clickedItemType === 'room' ||
            clickedItemType == 'zone' ||
            clickedItemType == 'line' ||
            (clickedItemType == 'poi' && isPoiFromBackend))
        "
        :buttonText="buttonText"
        :iconLeft="iconPlusSmall"
        :size="'small'"
        style="width: 100%"
        @onClicked="createTicket"
      />
    </div>
  </div>
  <div v-else>
    <div class="table-container">
      <div class="table__no-items">
        {{ t('general__text__not-available-for-this-type-object') }}
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import {
  archiveTicket,
  deleteTicket,
  getTicketsPage,
  Page,
  Ticket
} from '@/services/tickets.service'
import ButtonGroupNoIcons from '@/design-system/atoms/buttons/ButtonGroupNoIcons.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TableTicketsMini from '@/components/features/tickets/TableTicketsMini.vue'
import useReportPoi from '@/hooks/reports/useReportPoi'
import iconPlusSmall from '@/design-system/assets/icons/Icon-Plus-Small.svg'
import * as Sentry from '@sentry/vue'

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}
defineEmits<Emits>()
const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

let abortController: AbortController | null = null
const buttonText = `${t('general__data__create')} ${t('general__data__new-ticket').toLowerCase()}`
const includeArchivedTickets = ref(false) // Archived and deleted tickets are expected to have the property active: false
const includeChildObject = ref(true)
const includeFinishedTickets = ref(false)
const isLoadingFirstPage = ref(false)
const { isPoiFromBackend } = useReportPoi()
const pageNumber = ref(0)
const showConfirmDelete = ref(false)
const totalNumberOfItems = ref(0)
const totalNumberOfPages = ref(0)
const tabOptions = ref([
  {
    title: t('general__text__priority'),
    value: 'priority',
    active: true,
    disabled: false
  },
  {
    title: t('general__text__status'),
    value: 'status',
    active: false,
    disabled: false
  },
  {
    title: t('general__text__due-date'),
    value: 'dueDate',
    active: false,
    disabled: false
  }
])
const activeTab = computed(() => {
  const activeTab = tabOptions.value.find((option) => option.active)
  return activeTab!.value
})
const clickedItemType = computed(() => {
  return store.state.clickedItemType
})
const currentOverlayModalPinia = computed(() => {
  return piniaStore.currentOverlayModalPinia
})
const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
const selectedObject = computed(() => {
  return store.getters.selectedObject
})
const selectedTicket = computed(() => piniaStore.selectedTicket)
const showOverlayModalPinia = computed(() => {
  return piniaStore.showOverlayModalPinia
})
const sortOrder = computed(() => {
  return activeTab.value === 'priority' ? 'desc' : 'asc'
})
const ticketsList = computed(() => {
  return piniaStore.ticketsList
})
const fields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: ticketsList.value
        ? totalNumberOfItems.value + ' ' + t('general__label__tickets').toLocaleLowerCase()
        : ''
    },
    {
      key: 'sorting',
      name: getActiveTabName()
    }
  ]
  return fields
})
function changeTab(value) {
  tabOptions.value = tabOptions.value.map((el) => {
    el.active = el.value === value
    return el
  })
}
async function archiveSelectedTicket() {
  if (selectedTicket.value)
    try {
      const ticket = await archiveTicket(selectedTicket.value?.id)
      if (ticket) {
        setInfoMessage('success', t('general__data__ticket-archived'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== ticket.id)
        piniaStore.setTicketsList([...ticketsArray])
        piniaStore.setSelectedTicket(null)
        totalNumberOfItems.value--
      }
    } catch (error) {
      Sentry.captureException(error)
      setInfoMessage('error', 'general__data__ticket-archive-failed')
    }
}
function createTicket() {
  piniaStore.setSelectedTicket(null)
  piniaStore.setCurrentOverlayModalPinia('createTicket')
  piniaStore.setShowOverlayModalPinia(true)
}
async function deleteSelectedTicket() {
  if (selectedTicket.value) {
    const ticketId = selectedTicket.value.id
    try {
      const res = await deleteTicket(ticketId)
      if (res >= 200 && res < 300) {
        setInfoMessage('success', t('general__data__ticket-deleted'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== ticketId)
        piniaStore.setTicketsList([...ticketsArray])
        piniaStore.setSelectedTicket(null)
        totalNumberOfItems.value--
      }
    } catch (error) {
      Sentry.captureException(error)
      setInfoMessage('error', t('general__data__ticket-delete-failed'))
    }
  }
}
async function loadMoreTickets() {
  if (pageNumber.value === totalNumberOfPages.value) {
    return
  }
  if (!store.getters.selectedObjectId) return
  pageNumber.value++
  const ticketsPage: Page<Ticket> | undefined | 'canceled' = await getTicketsPage(
    includeChildObject.value,
    includeFinishedTickets.value,
    includeArchivedTickets.value,
    pageNumber.value,
    25,
    {
      entityId: store.getters.selectedObjectId
    },
    `${activeTab.value},${sortOrder.value}`
  )
  if (ticketsPage !== 'canceled' && ticketsPage?.content?.length) {
    piniaStore.setTicketsList([...ticketsList.value, ...ticketsPage.content])
  }
}
function getActiveTabName() {
  if (activeTab.value === 'status') return t('general__text__status')
  if (activeTab.value === 'priority') return t('general__text__priority')
  if (activeTab.value === 'dueDate') return t('general__text__due-date')
  return ''
}
async function refreshList() {
  if (abortController) abortController.abort()
  if (!store.getters.selectedObjectId) return
  abortController = new AbortController()
  piniaStore.setSelectedTicket(null)
  piniaStore.setTicketsList([])
  pageNumber.value = 0
  totalNumberOfPages.value = 0
  totalNumberOfItems.value = 0
  isLoadingFirstPage.value = true
  const ticketsPage: Page<Ticket> | undefined | 'canceled' = await getTicketsPage(
    includeChildObject.value,
    includeFinishedTickets.value,
    includeArchivedTickets.value,
    pageNumber.value,
    25,
    {
      entityId: store.getters.selectedObjectId
    },
    `${activeTab.value},${sortOrder.value}`,
    abortController.signal
  )
  if (ticketsPage !== 'canceled' && ticketsPage?.content?.length) {
    totalNumberOfPages.value = ticketsPage.totalPages
    totalNumberOfItems.value = ticketsPage.totalElements
    piniaStore.setTicketsList(ticketsPage.content)
  }
  if (ticketsPage !== 'canceled') isLoadingFirstPage.value = false
}
function setInfoMessage(type: string, message: string) {
  store.commit('setInfoMessage', { message, type })
}

watch(activeTab, () => {
  refreshList()
})
watch(includeChildObject, () => {
  refreshList()
})
watch(includeFinishedTickets, () => {
  refreshList()
})
watch(
  selectedObject,
  async (newSelection, oldSelection) => {
    if (newSelection === 0) {
      // Don't include child objects or finished tickets for realm, if activated for realm in the future
      includeChildObject.value = false
      includeFinishedTickets.value = false
    }
    if (newSelection !== 0 && oldSelection === 0) {
      // If realm was previously selected, include child objects for the new selection
      includeChildObject.value = true
    }
    if (newSelection !== 0) {
      refreshList()
    }
  },
  { immediate: true }
)
watch(showOverlayModalPinia, () => {
  if (currentOverlayModalPinia.value === 'createTicket' && !showOverlayModalPinia.value) {
    refreshList()
  }
})
onBeforeUnmount(() => {
  if (abortController) abortController.abort()
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.overlay-confirm-delete {
  left: -72px;
  top: -16px;
  &__mobile,
  &__fullscreen {
    left: 0;
    top: 0;
  }
}
.spinner {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-top: 80px;
  &__fullscreen {
    padding-bottom: 15%;
  }
  &__mobile {
    padding-bottom: 50%;
  }
}
#tickets-dashboard {
  background-color: #fff;
  border-bottom-right-radius: variables.$border-radius-small;
  border-bottom-left-radius: variables.$border-radius-small;
  display: flex;
  flex-flow: column;
  height: fit-content;
  max-height: calc(
    100vh - #{variables.$layout-top-bar-height} - #{variables.$padding-xlarge} - 152px
  );
  min-height: 400px;
  overflow: hidden;
  width: 100%;

  // &.fullscreen,
  &.mobile {
    border-radius: 0;
    bottom: 0;
    height: 100%;
    left: 0;
    position: fixed;
    right: 0;
    top: calc(#{variables.$layout-top-bar-height} + 56px);
    max-height: calc(100vh - #{variables.$layout-top-bar-height} - 56px);
    width: 100%;
  }
}
.bottom-container {
  background-color: #fff;
  border-top: 1px solid variables.$color-greyscale-line-grey;
  display: flex;
  flex: 0 0 72px;
  flex-flow: column;
  height: 72px;
  justify-self: flex-end;
  margin-top: auto;
  padding: 16px variables.$padding-main-body;
  &__no-button {
    border-top: none;
    flex: 0 0 24px;
    height: 24px;
    padding: 0;
  }
}
.button-group-container {
  display: flex;
  flex: 0 0 64px;
  flex-flow: column;
  height: 64px;
  padding: 16px variables.$padding-main-body;
}
.include-object-container {
  align-items: top;
  display: flex;
  flex: 0 0 40px;
  height: 40px;
  padding: 0 24px;
  &__checkbox {
    margin-left: -3px;
    margin-right: 5px;
    padding: 0;
  }
  &__checkbox-placeholder {
    border-radius: 4px;
    border: 1px solid #767676;
    cursor: not-allowed;
    height: 18px;
    margin-right: 8px;
    margin-top: 3px;
    width: 18px;
  }
  &__info {
    height: 24px;
  }
  &__text {
    color: variables.$color-text-secondary;
    padding-right: 4px;
    @include font-mixins.desktop-regular-paragraph;
  }
}
.table-container {
  display: contents;
  .table__no-items {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    color: variables.$color-greyscale-content;
    @include font-mixins.desktop-regular-paragraph;
    height: 100%;
    justify-content: flex-start;
    padding: 16px variables.$padding-main-body;
  }
}
</style>
